// Modal „Přidáno do košíku" — redesign nativního Shoptet „advanced order"
// overlaye dle design_handoff_kosik_modal (desktop = varianta 2a, mobil = 2b
// bottom sheet). Nahrazuje cartToast.js jako feedback po addToCart.
//
// Mechanika (ověřeno sondou na dev 2026-08-24):
//   • Default JS po addCartItem stáhne `/action/Cart/GetExtendedOrder/`
//     a otevře obsah v colorboxu (`#colorbox.shoptet-modal-lg`); po načtení
//     vystřelí `ShoptetDOMAdvancedOrderLoaded` — to je náš hook. Nativní
//     colorbox schováváme (body.bs-ao-lock, třída se zvedá už při kliku na
//     add-to-cart, aby nebliknul loading stav) a hned ho zavíráme.
//   • Nativní stepper v `.ao-quantity` POSTuje setCartItemAmount sám (delegace
//     default JS) → formulář se do našeho modalu PŘESOUVÁ (appendChild, ne
//     klon — přesun zachová listenery i delegaci), jen se restyluje.
//   • Popup se po změně množství NEpřerenderuje a `ShoptetCartUpdated` na
//     detailu vůbec nelétá — přepočty jedou na `ShoptetDataLayerUpdated` /
//     `ShoptetDOMCartCountUpdated` z dataLayer `cartInfo`
//     (getNoBillingShippingPrice = zboží, leftToFreeShipping, freeShippingFrom).
//   • Upsell = karty z nativního bloku „Ostatní zákazníci také nakoupili"
//     (`.products-related`); přidání přes shoptet.cartShared.addToCart —
//     modal zůstává otevřený, nativní re-fetch popupu se jen tiše zavře.
//
// Doprava zdarma platí v tomhle shopu JEN pro výdejní místa (rozhodnutí
// 2026-08-21, viz checkoutPage.js FREE_SHIP_SCOPE) → texty nesou rozsah
// „na výdejní místo", i když handover psal jen „dopravu máte zdarma".
import { hasShoptet, shoptetData, addToCart, cartLeft } from '../shoptet-hooks.js';
import { createSaleEnd, saleEndFor } from './saleEnd.js';
import { icon } from './icon.js';
import { money } from './money.js';
import { listingDispatch } from './deliveryTimeline.js';
import { cutoffLabel } from './dispatchEta.js';
import { discountPercent } from './productPrice.js';
import { page } from './pages.js';
import { shippingPromo } from './shippingModel.js';
import L from '../locale/cs/cartModal.js';

const NBSP = '\u00A0';
const MOBILE_MQ = '(max-width: 1023px)';
const LOCK_TTL_MS = 5000; // pojistka: bez příchodu popupu se colorbox zase ukáže
const ADD_GAP_MS = 900; // rozestup addToCart (stejně jako upsell na detailu)
const ADDED_FLASH_MS = 1500;

const TITLE = L.title;
const BACK_LABEL = L.back;
const CART_LABEL = L.cart;
const UPSELL_TITLE = L.upsellTitle;
const FREE_SHIP_SCOPE = L.freeShipScope;

const priceText = (value) => money(value, true);

function parsePrice(text) {
  const m = (text || '').replace(/[\s\u00A0]/g, '').match(/(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) : null;
}

function reducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isMobile() {
  return window.matchMedia && window.matchMedia(MOBILE_MQ).matches;
}

function node(tag, className, text) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text != null) el.textContent = text;
  return el;
}

// „Skladem (>5 ks) · odesíláme dnes při objednání do 15:00" — druhá část ze
// sdíleného pravidla karty výpisu (listingDispatch: uzávěrka 15:00, v pátek 14:30,
// vč. svátků, v okně nedělního svozu PPL neděle).
function etaText() {
  const now = new Date();
  const eta = listingDispatch(now);
  if (eta.days === 0) return L.sendsToday(cutoffLabel(now));
  // „Expedujeme zítra" / „Expedujeme v pondělí" → „odesíláme …"
  return L.sendsFrom(eta.label);
}

// --- čtení stavu košíku z dataLayeru ---------------------------------------

function cartTotals() {
  const info = shoptetData('cartInfo') || {};
  const price = info.getNoBillingShippingPrice;
  const goods = price ? Number(price.withVat) : NaN;
  let count = null;
  try {
    const items = window.getShoptetDataLayer && window.getShoptetDataLayer('cart');
    if (Array.isArray(items)) count = items.length;
  } catch (e) {
    /* dataLayer nedostupný */
  }
  // doprava zdarma — práh jen z živých dat; `freeShippingFrom` je null u košíku
  // s nadrozměrem (vyloučen z dopravy zdarma) → zóna se pak nekreslí, práh by lhal.
  // `leftToFreeShipping.priceLeft` je v takovém košíku taky null → `cartLeft`
  // vrací NaN (NEZNÁMO), ne nulu; jinak by z toho vyšlo „práh dosažen".
  const shipFrom = Number(info.freeShippingFrom) || null;
  const leftRaw = cartLeft(info, 'leftToFreeShipping');
  const shipLeft = Number.isFinite(leftRaw) ? leftRaw : shipFrom && Number.isFinite(goods) ? shipFrom - goods : null;
  // dosažení prahu se smí tvrdit jen tam, kde práh vůbec platí (shipFrom),
  // nebo když to Shoptet řekne sám (`freeShipping: true`)
  const freeShip = info.freeShipping === true || (shipFrom != null && shipLeft != null && shipLeft <= 0);
  const leftText =
    (info.leftToFreeShipping && info.leftToFreeShipping.formattedPrice) ||
    (shipLeft != null && shipLeft > 0 ? priceText(shipLeft) : null);
  return {
    goods: Number.isFinite(goods) ? goods : null,
    count: count,
    shipFrom: shipFrom,
    shipLeft: shipLeft,
    leftText: leftText,
    freeShip: freeShip,
  };
}

// --- extrakce dat z nativního advanced-order obsahu -------------------------

function imgSrcOf(img) {
  if (!img) return null;
  const lazy = img.getAttribute('data-src');
  return lazy || img.getAttribute('src') || null;
}

/** Položka košíku z dataLayeru podle názvu — advanced-order markup SKU nenese. */
function cartItemByName(name) {
  const info = shoptetData('cartInfo');
  const items = info && Array.isArray(info.cartItems) ? info.cartItems : [];
  const wanted = String(name || '').replace(/\s+/g, ' ').trim();
  if (!wanted) return null;
  for (let i = 0; i < items.length; i++) {
    if (String(items[i].name || '').replace(/\s+/g, ' ').trim() === wanted) return items[i];
  }
  return null;
}

function extractProduct(root) {
  const wrap = root.querySelector('.ao-product');
  if (!wrap) return null;
  const nameA = wrap.querySelector('.ao-name a');
  const availStrong = wrap.querySelector('.ao-availability strong');
  const availAmount = wrap.querySelector('.ao-availability .availability-amount');
  const priceStrong = wrap.querySelector('.ao-price strong');
  const availText = ((availStrong ? availStrong.textContent : '') + ' ' + (availAmount ? availAmount.textContent : ''))
    .replace(/\s+/g, ' ')
    .trim();
  return {
    url: nameA ? nameA.getAttribute('href') : null,
    name: nameA ? nameA.textContent.trim() : '',
    img: imgSrcOf(wrap.querySelector('.ao-image img')),
    availText: availText,
    inStock: L.inStockRe.test(availText),
    unitPrice: parsePrice(priceStrong && priceStrong.textContent),
    qtyForm: wrap.querySelector('.ao-quantity form'),
  };
}

function extractUpsell(root) {
  const out = [];
  const cards = root.querySelectorAll('.products-related .product .p');
  for (const p of cards) {
    if (out.length >= 8) break;
    const nameA = p.querySelector('a.name');
    // text celého boxu, ne prvního <span> — karty v popupu stihne dekorovat
    // productCard.js (ShoptetDOMContentLoaded letí dřív) a vloží na začátek
    // ikonový span bez textu
    const avail = p.querySelector('.availability');
    const availText = avail ? avail.textContent.replace(/\s+/g, ' ').trim() : '';
    const codeEl = p.querySelector('.p-code [data-micro="sku"]');
    const flags = p.querySelector('.flags');
    let badge = null;
    if (flags) {
      if (flags.querySelector('.flag-new')) badge = { label: L.flagNew, mod: 'new' };
      else if (flags.querySelector('.flag-tip')) badge = { label: L.flagTip, mod: 'tip' };
      else if (flags.querySelector('.flag-action')) badge = { label: L.flagAction, mod: 'tip' };
    }
    out.push({
      url: nameA ? nameA.getAttribute('href') : null,
      name: nameA ? nameA.textContent.trim() : '',
      img: imgSrcOf(p.querySelector('a.image img')),
      priceF: (p.querySelector('.price-final strong') || { textContent: '' }).textContent.trim(),
      code: codeEl ? codeEl.textContent.trim() : null,
      inStock: L.inStockRe.test(availText),
      hasForm: !!p.querySelector('form.pr-action'),
      badge: badge,
    });
  }
  return out;
}

function cartHref(root) {
  const a = root.querySelector('.advancedOrder__buttons a[href]');
  return (a && a.getAttribute('href')) || document.querySelector('a.cart-count')?.getAttribute('href') || page('cart');
}

// --- modul ------------------------------------------------------------------

export function initCartModal() {
  if (document.body.dataset.bsCartModal) return;
  document.body.dataset.bsCartModal = '1';
  // Bez živého Shoptetu (offline fixtures) advanced order neexistuje — modul
  // se jen tiše nenaváže.
  if (!hasShoptet()) return;

  let st = null; // stav otevřeného modalu
  let lockTimer = null;
  let pendingAddUntil = 0;

  // Nativní colorbox s popupem schovat DŘÍV, než se stihne ukázat loading stav:
  // třída jde nahoru už při kliku na add-to-cart (capture, chytá i nativní
  // tlačítka mimo naši vrstvu), pojistkový timer ji zase sundá, kdyby popup
  // nedorazil (např. neúspěšný add).
  function lockNative() {
    document.body.classList.add('bs-ao-lock');
    clearTimeout(lockTimer);
    lockTimer = setTimeout(function () {
      if (!st) document.body.classList.remove('bs-ao-lock');
    }, LOCK_TTL_MS);
  }

  function unlockNative() {
    clearTimeout(lockTimer);
    document.body.classList.remove('bs-ao-lock');
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('.add-to-cart-button, [data-testid="buttonAddToCart"]')) lockNative();
    },
    true
  );

  function closeNativePopup() {
    try {
      if (window.shoptet && window.shoptet.modal && typeof window.shoptet.modal.close === 'function') {
        window.shoptet.modal.close();
        return;
      }
      if (window.jQuery && window.jQuery.colorbox) window.jQuery.colorbox.close();
    } catch (e) {
      /* colorbox není otevřený */
    }
  }

  document.addEventListener('ShoptetDOMAdvancedOrderLoaded', function () {
    const src = document.querySelector('#colorbox .advanced-order, #cboxLoadedContent .advanced-order');
    if (!src) return;
    if (st) {
      // upsell add za otevřeného modalu: nativní re-fetch jen zavřít, náš modal
      // se přepočte z dataLayeru (ShoptetDataLayerUpdated už doběhl/doběhne)
      closeNativePopup();
      refresh();
      return;
    }
    const product = extractProduct(src);
    if (!product) {
      // neznámý obsah → nechat nativní popup žít (degradace, ne mrtvý overlay)
      unlockNative();
      return;
    }
    open(product, extractUpsell(src), cartHref(src));
    closeNativePopup();
  });

  // --- stavba ---------------------------------------------------------------

  function shipZone() {
    const zone = node('div', 'bs-ao__ship');
    const badge = node('span', 'bs-ao__shipicon');
    badge.appendChild(icon('truck'));
    const txt = node('div', 'bs-ao__shiptxt');
    const msg = node('span', 'bs-ao__shipmsg');
    const bar = node('span', 'bs-ao__bar');
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-label', L.shipProgress);
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    const fill = node('span', 'bs-ao__barfill');
    bar.appendChild(fill);
    txt.appendChild(msg);
    txt.appendChild(bar);
    // akce na dopravu pod progressem (#322, mock M11) — plní refresh()
    const note = node('span', 'bs-ao__shipnote');
    note.hidden = true;
    txt.appendChild(note);
    zone.appendChild(badge);
    zone.appendChild(txt);
    return { zone: zone, msg: msg, bar: bar, fill: fill, note: note };
  }

  function backButton(className) {
    const btn = node('button', className);
    btn.type = 'button';
    btn.appendChild(icon('arrow-left'));
    btn.appendChild(document.createTextNode(BACK_LABEL));
    btn.addEventListener('click', closeUi);
    return btn;
  }

  function buildCard(item) {
    const card = node('article', 'bs-ao__card');
    const imgA = node('a', 'bs-ao__cardimg');
    if (item.url) imgA.href = item.url;
    if (item.img) {
      const img = node('img');
      img.src = item.img;
      img.alt = '';
      img.loading = 'lazy';
      imgA.appendChild(img);
    }
    if (item.badge) {
      imgA.appendChild(node('span', 'bs-ao__flag bs-ao__flag--' + item.badge.mod, item.badge.label));
    }
    card.appendChild(imgA);
    const nameA = node('a', 'bs-ao__cardname', item.name);
    if (item.url) nameA.href = item.url;
    card.appendChild(nameA);
    const meta = node('div', 'bs-ao__cardmeta');
    meta.appendChild(node('strong', 'bs-ao__cardprice', item.priceF));
    if (item.inStock) {
      const stock = node('span', 'bs-ao__cardstock');
      stock.appendChild(icon('check'));
      stock.appendChild(document.createTextNode(L.inStock));
      meta.appendChild(stock);
    }
    card.appendChild(meta);
    // produkt s variantami nemá v related kartě add formulář → CTA vede na detail
    if (item.hasForm && item.code) {
      const cta = node('button', 'bs-ao__cardcta');
      cta.type = 'button';
      cta.appendChild(icon('cart'));
      cta.appendChild(node('span', null, L.addToCart));
      cta.addEventListener('click', function () {
        if (Date.now() < pendingAddUntil) return;
        pendingAddUntil = Date.now() + ADD_GAP_MS;
        lockNative();
        addToCart({ productCode: item.code, amount: 1 });
        cta.classList.add('is-added');
        cta.disabled = true;
        cta.innerHTML = '';
        cta.appendChild(icon('check'));
        cta.appendChild(node('span', null, L.added));
        setTimeout(function () {
          cta.classList.remove('is-added');
          cta.disabled = false;
          cta.innerHTML = '';
          cta.appendChild(icon('cart'));
          cta.appendChild(node('span', null, L.addToCart));
        }, ADDED_FLASH_MS);
      });
      card.appendChild(cta);
    } else {
      const cta = node('a', 'bs-ao__cardcta bs-ao__cardcta--link');
      if (item.url) cta.href = item.url;
      cta.appendChild(node('span', null, L.chooseVariant));
      card.appendChild(cta);
    }
    return card;
  }

  function build(product, upsell, kosikUrl) {
    const root = node('div', 'bs-ao');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', TITLE);

    const scrim = node('div', 'bs-ao__scrim');
    scrim.addEventListener('click', closeUi);
    root.appendChild(scrim);

    const panel = node('div', 'bs-ao__panel');
    root.appendChild(panel);

    // grabber (mobil) — zároveň úchyt pro swipe-down
    const grab = node('div', 'bs-ao__grab');
    grab.appendChild(node('span', 'bs-ao__grabber'));
    panel.appendChild(grab);

    // hlavička
    const head = node('header', 'bs-ao__head');
    const okBadge = node('span', 'bs-ao__badge');
    okBadge.appendChild(icon('check'));
    head.appendChild(okBadge);
    const headTxt = node('div', 'bs-ao__headtxt');
    headTxt.appendChild(node('strong', 'bs-ao__title', TITLE));
    const sub = node('span', 'bs-ao__sub');
    sub.setAttribute('aria-live', 'polite');
    headTxt.appendChild(sub);
    head.appendChild(headTxt);
    const close = node('button', 'bs-ao__close');
    close.type = 'button';
    close.setAttribute('aria-label', L.close);
    close.appendChild(icon('close'));
    close.addEventListener('click', closeUi);
    head.appendChild(close);
    panel.appendChild(head);

    // scrollovatelné tělo (na mobilu; na desktopu se prostě neroluje)
    const body = node('div', 'bs-ao__body');
    panel.appendChild(body);

    // produkt
    const prod = node('div', 'bs-ao__product');
    const imgA = node('a', 'bs-ao__img');
    if (product.url) imgA.href = product.url;
    if (product.img) {
      const img = node('img');
      img.src = product.img;
      img.alt = product.name;
      imgA.appendChild(img);
    }
    prod.appendChild(imgA);
    const pInfo = node('div', 'bs-ao__pinfo');
    const pName = node('a', 'bs-ao__pname', product.name);
    if (product.url) pName.href = product.url;
    pInfo.appendChild(pName);
    const avail = node('span', 'bs-ao__avail' + (product.inStock ? '' : ' bs-ao__avail--off'));
    if (product.inStock) avail.appendChild(icon('check'));
    avail.appendChild(document.createTextNode(product.availText));
    if (product.inStock) {
      avail.appendChild(node('span', 'bs-ao__eta', ' · ' + etaText()));
    }
    pInfo.appendChild(avail);
    prod.appendChild(pInfo);
    // Třída `ao-product` je NUTNÁ: delegovaný handler default JS (na <html>)
    // posílá setCartItemAmount jen, když má input.amount předka .cart-table /
    // .cart-widget-product-amount / .ao-product — bez ní stepper jen mění číslo
    // a košík se tiše rozjede od zobrazené hodnoty (ověřeno v main-3g.js).
    const qtySlot = node('div', 'bs-ao__qty ao-product');
    // PŘESUN nativního formuláře, ne klon — přesun zachová listenery
    if (product.qtyForm) qtySlot.appendChild(product.qtyForm);
    prod.appendChild(qtySlot);
    const price = node('strong', 'bs-ao__price');
    // původní cena + „−34 %" nad částkou a řádek „Ušetříte X Kč" pod ní
    // (zadání 2026-08-31) — plní refresh() z dataLayeru, bez slevy zůstávají
    // skryté; sourozenec = konec akce
    const was = node('span', 'bs-ao__price-was');
    was.hidden = true;
    price.appendChild(was);
    price.appendChild(node('span', 'bs-ao__price-val')); // částku plní refresh()
    const save = node('span', 'bs-ao__save');
    save.hidden = true;
    price.appendChild(save);
    prod.appendChild(price);
    body.appendChild(prod);
    // konec akční ceny pod cenou položky: kód i slevu dává dataLayer (položka
    // košíku podle názvu — advanced-order markup SKU nenese), konec index
    const item = cartItemByName(product.name);
    if (item && item.code && Number(item.priceWithoutDiscount) > Number(item.priceWithVat)) {
      saleEndFor(item.code).then(function (toDate) {
        if (!toDate || price.querySelector('.bs-ao__sale')) return;
        const el = createSaleEnd(toDate, { className: 'bs-ao__sale' });
        if (el) price.appendChild(el);
      });
    }

    // pás dopravy zdarma + navigace
    const band = node('div', 'bs-ao__band');
    band.appendChild(backButton('bs-ao__back'));
    const ship = shipZone();
    band.appendChild(ship.zone);
    const cta = node('a', 'bs-ao__cta', CART_LABEL);
    cta.href = kosikUrl;
    cta.appendChild(icon('arrow-right'));
    band.appendChild(cta);
    body.appendChild(band);

    // upsell
    let track = null;
    let prev = null;
    let next = null;
    if (upsell.length) {
      const up = node('div', 'bs-ao__upsell');
      const upHead = node('div', 'bs-ao__uphead');
      upHead.appendChild(node('strong', 'bs-ao__uptitle', UPSELL_TITLE));
      prev = node('button', 'bs-ao__nav bs-ao__nav--prev');
      prev.type = 'button';
      prev.setAttribute('aria-label', L.prev);
      prev.appendChild(icon('arrow-left'));
      next = node('button', 'bs-ao__nav bs-ao__nav--next');
      next.type = 'button';
      next.setAttribute('aria-label', L.next);
      next.appendChild(icon('arrow-right'));
      upHead.appendChild(prev);
      upHead.appendChild(next);
      up.appendChild(upHead);
      track = node('div', 'bs-ao__track');
      for (const item of upsell) track.appendChild(buildCard(item));
      up.appendChild(track);
      body.appendChild(up);

      const slide = function (dir) {
        track.scrollBy({ left: dir * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', function () {
        slide(-1);
      });
      next.addEventListener('click', function () {
        slide(1);
      });
      const syncNav = function () {
        const maxLeft = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= maxLeft;
      };
      track.addEventListener('scroll', syncNav, { passive: true });
      requestAnimationFrame(syncNav);
    }

    // sticky patička (mobil)
    const foot = node('footer', 'bs-ao__foot');
    const mCta = node('a', 'bs-ao__cta bs-ao__cta--wide');
    mCta.href = kosikUrl;
    mCta.appendChild(icon('cart'));
    mCta.appendChild(node('span', 'bs-ao__ctalabel'));
    foot.appendChild(mCta);
    foot.appendChild(backButton('bs-ao__back bs-ao__back--foot'));
    panel.appendChild(foot);

    return {
      root: root,
      panel: panel,
      grab: grab,
      head: head,
      close: close,
      sub: sub,
      price: price,
      ship: ship,
      band: band,
      mCtaLabel: mCta.querySelector('.bs-ao__ctalabel'),
      qtyInput: qtySlot.querySelector('input.amount'),
      qtySlot: qtySlot,
    };
  }

  // --- přepočty -------------------------------------------------------------

  function refresh() {
    if (!st) return;
    const t = cartTotals();
    const qty = st.ui.qtyInput ? Number(st.ui.qtyInput.value) || 1 : 1;

    // hlavička: „V košíku máte 2 položky za 1 298 Kč"
    if (t.count != null && t.goods != null) {
      st.ui.sub.textContent = L.inCart(t.count, priceText(t.goods));
    } else {
      st.ui.sub.textContent = '';
    }

    // cena za přidanou položku (jednotková × množství)
    (st.ui.price.querySelector('.bs-ao__price-val') || st.ui.price).textContent =
      st.product.unitPrice != null ? priceText(st.product.unitPrice * qty) : '';

    // původní cena, procento a ušetřená částka (zadání 2026-08-31) — jednotkové
    // ceny z dataLayeru (položka podle názvu, advanced-order markup SKU nenese);
    // procento sdílí floor pravidlo Shoptetu (productPrice.discountPercent)
    const wasEl = st.ui.price.querySelector('.bs-ao__price-was');
    const saveEl = st.ui.price.querySelector('.bs-ao__save');
    if (wasEl && saveEl) {
      const item = cartItemByName(st.product.name);
      const was = item ? Number(item.priceWithoutDiscount) : NaN;
      const unit = item ? Number(item.priceWithVat) : NaN;
      const discounted = Number.isFinite(was) && Number.isFinite(unit) && was > unit;
      wasEl.hidden = !discounted;
      saveEl.hidden = !discounted;
      if (discounted) {
        wasEl.textContent = '';
        wasEl.appendChild(node('s', null, priceText(was * qty)));
        const pct = discountPercent(was, unit);
        if (pct != null) wasEl.appendChild(node('b', null, '−' + pct + NBSP + '%'));
        saveEl.textContent = L.save(priceText((was - unit) * qty));
      }
    }

    // progress dopravy zdarma
    const s = st.ui.ship;
    if (!t.shipFrom && !t.freeShip) {
      // košík s nadrozměrem: práh neexistuje (freeShippingFrom je null),
      // tvrdit „od 2 000 Kč" by lhalo → zóna pryč; na mobilu tím pás osiří
      // (back i CTA nese patička) → schovat celý
      s.zone.hidden = true;
      st.ui.band.classList.add('bs-ao__band--noship');
    } else {
      st.ui.band.classList.remove('bs-ao__band--noship');
      s.zone.hidden = false;
      if (t.freeShip) {
        s.msg.className = 'bs-ao__shipmsg bs-ao__shipmsg--free';
        s.msg.textContent = L.freeShipDone(FREE_SHIP_SCOPE);
        s.fill.style.width = '100%';
        s.bar.setAttribute('aria-valuenow', '100');
      } else {
        s.msg.className = 'bs-ao__shipmsg';
        s.msg.innerHTML = '';
        // zpráva je flex řada (jako v mocku) → mezery nesou nbsp uvnitř <b>,
        // holé mezery mezi flex položkami by se sesypaly
        s.msg.appendChild(document.createTextNode(L.leftPre));
        s.msg.appendChild(node('b', null, NBSP + (t.leftText || '') + NBSP));
        s.msg.appendChild(document.createTextNode(L.leftMid(FREE_SHIP_SCOPE)));
        s.msg.appendChild(node('b', 'bs-ao__shipmsg-green', NBSP + L.leftFree));
        const pct =
          t.shipFrom && t.goods != null
            ? Math.max(0, Math.min(100, Math.round((t.goods / t.shipFrom) * 100)))
            : 0;
        s.fill.style.width = pct + '%';
        s.bar.setAttribute('aria-valuenow', String(pct));
      }
    }
    // Akce na dopravu (#322): jen košík s prahem dopravy zdarma (do 10 kg,
    // bez nadrozměru), který ho ještě nedosáhl — nad prahem je doprava zdarma
    // a těžký košík akci nemá.
    const promo = t.shipFrom && !t.freeShip ? shippingPromo() : null;
    s.note.hidden = !promo;
    if (promo) s.note.textContent = L.promoNote(promo.carrier, priceText(promo.price), promo.untilLabel);

    // mobilní CTA „Nákupní košík · 1 298 Kč"
    st.ui.mCtaLabel.textContent =
      CART_LABEL + (t.goods != null ? ' · ' + priceText(t.goods) : '');
  }

  function onDataLayer() {
    refresh();
  }

  // --- fokus, klávesnice, gesta --------------------------------------------

  function onKey(e) {
    if (!st) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeUi();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = [
      ...st.ui.root.querySelectorAll('button:not(:disabled), a[href], input, [tabindex]:not([tabindex="-1"])'),
    ].filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Swipe-down za grabber/hlavičku zavírá (sheet následuje prst; puštění pod
  // ~30 % výšky se vrací). Obsah těla scrolluje nezávisle — úchyt je jen
  // grabber + hlavička, takže se s vnitřním scrollem nepere.
  function bindSwipe(ui) {
    let startY = null;
    let dy = 0;
    const onMove = function (e) {
      if (startY == null) return;
      dy = Math.max(0, e.clientY - startY);
      ui.panel.style.transform = 'translateY(' + dy + 'px)';
      ui.panel.style.transition = 'none';
    };
    const onUp = function () {
      if (startY == null) return;
      const h = ui.panel.getBoundingClientRect().height;
      ui.panel.style.transition = '';
      startY = null;
      if (dy > h * 0.3) {
        closeUi();
      } else {
        ui.panel.style.transform = '';
      }
      dy = 0;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    const onDown = function (e) {
      if (!isMobile() || !e.isPrimary) return;
      startY = e.clientY;
      dy = 0;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };
    ui.grab.addEventListener('pointerdown', onDown);
    ui.head.addEventListener('pointerdown', function (e) {
      // tahat jde za hlavičku, ale ne za interaktivní prvky v ní
      if (e.target instanceof Element && e.target.closest('button, a')) return;
      onDown(e);
    });
  }

  function onPop() {
    if (!st) return;
    st.histPushed = false;
    destroy();
  }

  // --- otevření / zavření ---------------------------------------------------

  function open(product, upsell, kosikUrl) {
    const ui = build(product, upsell, kosikUrl);
    st = {
      ui: ui,
      product: product,
      histPushed: false,
      opener: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    };
    document.body.appendChild(ui.root);
    document.body.classList.add('bs-ao-open');
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('ShoptetDataLayerUpdated', onDataLayer);
    document.addEventListener('ShoptetDOMCartCountUpdated', onDataLayer);
    // klik na nativní +/- změní input hned, dataLayer dojede po AJAX — mezistav
    // přepočítáváme z inputu, ať cena neskáče pozdě
    if (ui.qtySlot) {
      ui.qtySlot.addEventListener('click', function () {
        setTimeout(refresh, 60);
      });
      if (ui.qtyInput) ui.qtyInput.addEventListener('change', refresh);
    }
    bindSwipe(ui);

    // Android zpět zavírá sheet
    if (isMobile()) {
      try {
        window.history.pushState({ bsAo: 1 }, '');
        st.histPushed = true;
      } catch (e) {
        /* file:// fixtures */
      }
    }
    window.addEventListener('popstate', onPop);

    refresh();

    if (reducedMotion()) {
      ui.root.classList.add('is-open');
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (st) st.ui.root.classList.add('is-open');
        });
      });
    }
    ui.close.focus();
  }

  function closeUi() {
    if (!st) return;
    if (st.histPushed) {
      st.histPushed = false;
      window.history.back(); // popstate dokončí úklid
    } else {
      destroy();
    }
  }

  function destroy() {
    if (!st) return;
    const s = st;
    st = null;
    document.removeEventListener('keydown', onKey, true);
    document.removeEventListener('ShoptetDataLayerUpdated', onDataLayer);
    document.removeEventListener('ShoptetDOMCartCountUpdated', onDataLayer);
    window.removeEventListener('popstate', onPop);
    document.body.classList.remove('bs-ao-open');
    closeNativePopup();
    unlockNative();
    const remove = function () {
      s.ui.root.remove();
    };
    if (reducedMotion()) {
      remove();
    } else {
      s.ui.root.classList.remove('is-open');
      s.ui.root.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 450); // pojistka, kdyby transitionend nedoběhl
    }
    if (s.opener && document.contains(s.opener)) s.opener.focus();
  }
}
